import Close from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Modal from '@mui/material/Modal'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import { SxProps } from '@mui/material/styles'
import { useTranslation } from 'next-i18next'
import React, { useId } from 'react'
import { iOS } from 'src/constants/device'
import useBreakpoints from '../hooks/useBreakpoints'

type IProps = {
  title?: string
  subtitle?: string
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any
  sx?: SxProps
  bigTitle?: boolean
  hideMd?: boolean
  width?: string
  bgColor?: string
  customTitle?: React.ReactNode
  fullScreen?: boolean
}

type BodyProps = IProps & {
  /** Id for the heading, so the dialog can be named by `aria-labelledby`. */
  titleId: string
}

/**
 * Shared primitive — ~60 consumers across public listing pages, linear flows
 * and dashboards — so the chrome speaks the vocabulary they all share: Rubik
 * heading in `ink-700`, `ink-200` subtitle, hairline + soft ink shadow on the
 * desktop panel, and a designed close control (40px, cream hover, blue-800
 * focus ring). Public props are unchanged.
 */
function Body({
  title,
  subtitle,
  children,
  setIsOpen,
  bigTitle,
  bgColor,
  customTitle,
  titleId
}: BodyProps) {
  const { t: tCommon } = useTranslation('common')
  return (
    <>
      <div
        className={`top-0 left-0 mb-0 flex w-full shrink-0 items-start justify-between ${bgColor ? bgColor : 'bg-white-500'} px-4 pt-4 pb-2 md:px-5 md:pt-5`}
      >
        <div className="flex-1">
          {title && !customTitle ? (
            <h2
              id={titleId}
              className={`text-lg leading-tight font-semibold text-ink-700 ${bigTitle ? 'md:text-2xl' : 'md:text-xl'}`}
            >
              {title}
            </h2>
          ) : (
            <div id={customTitle ? titleId : undefined}>{customTitle}</div>
          )}

          {subtitle && (
            <p className="mt-1 text-sm leading-snug text-ink-200">{subtitle}</p>
          )}
        </div>

        {/* Visible at every width: below `md` the sheet used to rely on
            swipe/backdrop alone, which screen-reader and keyboard users can't
            discover. Negative block margins on phones keep the header row
            about one title line tall, so sheets without a title (lead form,
            exit flow) barely grow. */}
        <div className="shrink-0">
          <IconButton
            aria-label={tCommon('A11Y.CLOSE_MODAL')}
            onClick={() => setIsOpen(false)}
            className="-my-1.5 -mr-1.5 size-9 text-ink-200 transition-colors hover:bg-cream-300 hover:text-ink-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 md:-mt-1 md:-mr-2 md:mb-0 md:size-10"
          >
            <Close fontSize="small" />
          </IconButton>
        </div>
      </div>

      <div
        className={`min-h-0 flex-1 [scrollbar-gutter:stable] overflow-y-auto ${bgColor ? bgColor : 'bg-white-500'} px-4 pt-0 pb-4 md:px-5 md:pb-5`}
      >
        {children}
      </div>
    </>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const modalStyles: any = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '1px solid',
  // `ink-100/15` — the hairline every other surface uses.
  borderColor: 'rgba(48, 70, 95, 0.15)',
  borderRadius: '16px',
  // Ink-tinted, not black: a floating panel still needs lift off the backdrop.
  boxShadow: '0 24px 48px -16px rgba(17, 29, 42, 0.28)',
  // MUI's focus trap focuses this container on open; it is not a control, so
  // the browser ring it drew around the whole panel was just noise. The close
  // button and fields keep their own focus-visible rings.
  outline: 'none'
}

function ModalDrawer({
  title,
  subtitle,
  isOpen,
  setIsOpen,
  children,
  sx,
  hideMd,
  bigTitle,
  width,
  bgColor,
  customTitle,
  fullScreen
}: IProps) {
  const { isUpMd, isDownMd } = useBreakpoints()
  const titleId = useId()
  if (isUpMd && hideMd) return null

  const container =
    typeof window !== 'undefined' ? () => window.document.body : undefined

  if (!isOpen) return null

  // Name the dialog after its heading (a custom title node counts). Without a
  // heading there is nothing to point at, so leave it unnamed.
  const dialogA11y = {
    role: 'dialog',
    'aria-modal': true,
    'aria-labelledby': title || customTitle ? titleId : undefined
  } as const

  return isDownMd ? (
    <SwipeableDrawer
      container={container}
      anchor="bottom"
      open={isOpen}
      onClose={() => setIsOpen(false)}
      onOpen={() => setIsOpen(true)}
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
      disableBackdropTransition={!iOS}
      disableDiscovery={!iOS}
      ModalProps={{ keepMounted: true }}
      slotProps={{
        paper: {
          ...dialogA11y,
          className: `rounded-t-2xl ${bgColor ? bgColor : 'bg-white-500'}`
        }
      }}
    >
      {/* Puller lives INSIDE the rounded body so the page-behind doesn't
          bleed through the gap above. Previously this was an absolutely
          positioned bar at `top-[-15px]` sitting on top of the drawer
          paper — that left a visible transparent strip between the puller
          and the rounded-corner body.

          Sticky positioning keeps the puller visible while the drawer
          body scrolls. Without `sticky top-0` the puller scrolled away
          with the content, leaving the user without the swipe-down
          affordance once they were past the first viewport. Same bg as
          the parent so content underneath doesn't peek through during
          scroll; sits above content via z-10. */}
      <div
        className={`max-h-[85dvh] [scrollbar-gutter:stable] overflow-y-auto rounded-t-2xl ${bgColor ? bgColor : 'bg-white-500'}`}
      >
        <div
          className={`sticky top-0 z-10 flex justify-center pt-2 pb-1 ${bgColor ? bgColor : 'bg-white-500'}`}
        >
          <div
            aria-hidden="true"
            className="h-1.5 w-12 rounded-full bg-ink-100/25"
          />
        </div>
        <Body
          title={title}
          setIsOpen={setIsOpen}
          isOpen={isOpen}
          subtitle={subtitle}
          bigTitle={bigTitle}
          bgColor={bgColor}
          customTitle={customTitle}
          titleId={titleId}
        >
          {children}
        </Body>
      </div>
    </SwipeableDrawer>
  ) : (
    <Modal open={isOpen} onClose={() => setIsOpen(false)}>
      <Box
        sx={{
          ...modalStyles,
          width: fullScreen ? '95%' : width ? width : '40%',
          maxWidth: fullScreen ? '95%' : '900px',
          minWidth: fullScreen ? '95%' : '500px',
          height: fullScreen ? '95%' : 'auto',
          maxHeight: fullScreen ? '95%' : '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          ...sx
        }}
        {...dialogA11y}
      >
        <Body
          title={title}
          subtitle={subtitle}
          setIsOpen={setIsOpen}
          isOpen={isOpen}
          bigTitle={bigTitle}
          bgColor={bgColor}
          customTitle={customTitle}
          titleId={titleId}
        >
          {children}
        </Body>
      </Box>
    </Modal>
  )
}

export default React.memo(ModalDrawer)
